import React, { useEffect, useMemo, useRef, useState } from 'react'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@apollo/client'
import { Alert, Button, Col, Popover, PopoverBody, PopoverHeader, Progress, Row } from 'reactstrap'
import './TablePreferencePicker.css'

const CURRENT_USER_QUERY = gql`
    query CurrentUserQuery {
        currentUser {
            id
            member {
                id
            }
        }
    }
`

const MEMBER_TABLE_PREFERENCE_QUERY = gql`
    query MemberTablePreferenceQuery($memberId: ID!, $tableId: String!) {
        memberTablePreference(memberId: $memberId, tableId: $tableId)
    }
`

const UPDATE_MEMBER_TABLE_PREFERENCE = gql`
    mutation updateMemberTablePreference($input: UpdateMemberTablePreferenceInput!) {
        updateMemberTablePreference(input: $input) {
            ok
            errors {
                messages
                field
            }
        }
    }
`

const getPreferenceSettings = (column = {}) => column.preferenceSettings || {}
const isColumnVisible = (column = {}) => {
    const preferenceSettings = getPreferenceSettings(column)
    if (typeof preferenceSettings.isVisible === 'boolean') return preferenceSettings.isVisible
    return false
}
const canToggleColumnVisibility = (column = {}) => {
    const preferenceSettings = getPreferenceSettings(column)
    if (typeof preferenceSettings.canToggleVisibility === 'boolean') return preferenceSettings.canToggleVisibility
    if (typeof preferenceSettings.canHide === 'boolean') return preferenceSettings.canHide
    if (typeof column.showInPicker === 'boolean') return column.showInPicker
    return false
}
const canReorderColumn = (column = {}) => {
    const preferenceSettings = getPreferenceSettings(column)
    if (typeof preferenceSettings.canReorder === 'boolean') return preferenceSettings.canReorder
    if (typeof preferenceSettings.positionIsFixed === 'boolean') return !preferenceSettings.positionIsFixed
    if (typeof column.positionIsFixed === 'boolean') return !column.positionIsFixed
    return false
}
const hasPickerControls = (column = {}) => canToggleColumnVisibility(column) || canReorderColumn(column)
const withPreferenceSettings = (column = {}, overrides = {}) => {
    const preferenceSettings = {
        ...getPreferenceSettings(column),
        ...overrides,
    }

    return {
        ...column,
        preferenceSettings,
    }
}

export const buildColumnConfigFromPreference = (defaultColumns = [], preferenceData) => {
    const fixedColumns = defaultColumns
        .filter((column) => !canReorderColumn(column))
        .map((column, index) => ({ ...withPreferenceSettings(column, { isVisible: true }), order: index }))

    const fixedIds = new Set(fixedColumns.map((column) => column.id))
    const knownMap = new Map(defaultColumns.map((column) => [column.id, column]))

    if (!Array.isArray(preferenceData) || preferenceData.length === 0) {
        const visibleColumns = defaultColumns
            .filter((column) => canReorderColumn(column))
            .map((column, index) => ({ ...withPreferenceSettings(column, { isVisible: true }), order: fixedColumns.length + index }))
        return [...fixedColumns, ...visibleColumns]
    }

    const visibleIds = new Set(preferenceData)
    const orderedVisibleColumns = preferenceData
        .filter((columnId) => !fixedIds.has(columnId))
        .map((columnId, index) => {
            if (knownMap.has(columnId)) {
                return { ...withPreferenceSettings(knownMap.get(columnId), { isVisible: true }), order: fixedColumns.length + index }
            }

            return {
                id: columnId,
                name: columnId,
                preferenceSettings: {
                    isVisible: true,
                    canToggleVisibility: true,
                    canReorder: true,
                },
                order: fixedColumns.length + index,
                isCustom: true,
            }
        })

    const hiddenColumns = defaultColumns
        .filter((column) => canReorderColumn(column) && !visibleIds.has(column.id))
        .map((column, index) => ({
            ...withPreferenceSettings(column, { isVisible: false }),
            order: fixedColumns.length + orderedVisibleColumns.length + index,
        }))

    return [...fixedColumns, ...orderedVisibleColumns, ...hiddenColumns]
}

function TableColumnPreferenceControl({ tableId, defaultColumnConfig = [], columnConfig = [], setColumnConfig, buttonLabel = 'PREFERENCE', placement = 'left' }) {
    const [isOpen, setIsOpen] = useState(false)
    const [error, setError] = useState(null)
    const [draggedColumnId, setDraggedColumnId] = useState(null)
    const [dropIndicatorIndex, setDropIndicatorIndex] = useState(null)
    const [draftColumnConfig, setDraftColumnConfig] = useState(columnConfig || [])
    const lastAppliedConfigSignatureRef = useRef(null)
    const applyColumnConfig = typeof setColumnConfig === 'function' ? setColumnConfig : () => {}

    const { data: currentUserData } = useQuery(CURRENT_USER_QUERY, {
        fetchPolicy: 'cache-first',
    })
    const memberId = currentUserData?.currentUser?.member?.id
    const normalizedDefaultColumns = useMemo(
        () => buildColumnConfigFromPreference(defaultColumnConfig, []),
        [defaultColumnConfig]
    )
    const defaultConfigSignature = useMemo(
        () => JSON.stringify(
            normalizedDefaultColumns.map((column) => ({
                id: column.id,
                isVisible: isColumnVisible(column),
                order: column.order,
            }))
        ),
        [normalizedDefaultColumns]
    )
    const draftConfigSignature = useMemo(
        () => JSON.stringify(
            draftColumnConfig.map((column) => ({
                id: column.id,
                isVisible: isColumnVisible(column),
                order: column.order,
            }))
        ),
        [draftColumnConfig]
    )

    const { data, loading, refetch } = useQuery(MEMBER_TABLE_PREFERENCE_QUERY, {
        variables: { memberId, tableId },
        skip: !memberId || !tableId,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    })

    const [updateMemberTablePreference, { loading: saving }] = useMutation(UPDATE_MEMBER_TABLE_PREFERENCE)

    useEffect(() => {
        let nextConfig = null

        if (!memberId) {
            nextConfig = normalizedDefaultColumns
        } else if (!loading && data) {
            nextConfig = buildColumnConfigFromPreference(defaultColumnConfig, data?.memberTablePreference)
        }

        if (!nextConfig) return

        const nextConfigSignature = JSON.stringify(
            nextConfig.map((column) => ({
                id: column.id,
                isVisible: isColumnVisible(column),
                order: column.order,
            }))
        )

        if (nextConfigSignature !== lastAppliedConfigSignatureRef.current) {
            lastAppliedConfigSignatureRef.current = nextConfigSignature
            applyColumnConfig(nextConfig)
            setDraftColumnConfig(nextConfig)
        }
    }, [memberId, loading, data, defaultColumnConfig, normalizedDefaultColumns, applyColumnConfig])

    useEffect(() => {
        if (!isOpen) {
            setDraftColumnConfig(columnConfig || [])
        }
    }, [columnConfig, isOpen])

    const openPopover = () => {
        setError(null)
        setDraftColumnConfig(columnConfig || [])
        setIsOpen(true)
    }

    const closePopover = () => {
        setError(null)
        setDraggedColumnId(null)
        setDropIndicatorIndex(null)
        setDraftColumnConfig(columnConfig || [])
        setIsOpen(false)
    }

    const togglePopover = () => {
        if (isOpen) {
            closePopover()
            return
        }
        openPopover()
    }

    const resetToDefault = () => {
        setError(null)
        setDraggedColumnId(null)
        setDropIndicatorIndex(null)
        setDraftColumnConfig(normalizedDefaultColumns)
    }

    const toggleColumnVisibility = (columnId) => {
        setDraftColumnConfig(
            draftColumnConfig.map((column) => ({
                ...withPreferenceSettings(
                    column,
                    column.id === columnId
                        ? { isVisible: !isColumnVisible(column) }
                        : { isVisible: isColumnVisible(column) }
                ),
            }))
        )
    }

    const handleDragStart = (event, index) => {
        if (!canReorderColumn(draftColumnConfig[index])) return
        event.dataTransfer.effectAllowed = 'move'
        setDraggedColumnId(draftColumnConfig[index]?.id || null)
        setDropIndicatorIndex(index)
    }

    const handleDragOver = (event, hoverIndex) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'

        if (!draggedColumnId) {
            setDropIndicatorIndex(hoverIndex)
            return
        }

        const dragIndex = draftColumnConfig.findIndex((column) => column.id === draggedColumnId)
        if (dragIndex === -1 || dragIndex === hoverIndex) {
            setDropIndicatorIndex(hoverIndex)
            return
        }
        if (!canReorderColumn(draftColumnConfig[dragIndex]) || !canReorderColumn(draftColumnConfig[hoverIndex])) return

        const updatedColumns = [...draftColumnConfig]
        const [draggedColumn] = updatedColumns.splice(dragIndex, 1)
        updatedColumns.splice(hoverIndex, 0, draggedColumn)

        setDraftColumnConfig(
            updatedColumns.map((column, index) => ({
                ...column,
                order: index,
            }))
        )
        setDropIndicatorIndex(hoverIndex)
    }

    const handleDrop = (event, dropIndex) => {
        event.preventDefault()
        setDropIndicatorIndex(dropIndex)
        setDraggedColumnId(null)
    }

    const handleDragEnd = () => {
        setDraggedColumnId(null)
        setDropIndicatorIndex(null)
    }

    const savePreference = async () => {
        if (!memberId || !tableId) return

        setError(null)
        const response = await updateMemberTablePreference({
            variables: {
                input: {
                    memberId,
                    tableId,
                    preferences: draftColumnConfig.filter((column) => isColumnVisible(column)).map((column) => column.id),
                },
            },
        }).catch((saveError) => {
            setError(saveError.message)
            return null
        })

        const result = response?.data?.updateMemberTablePreference
        if (!result) return

        if (result.ok) {
            applyColumnConfig(draftColumnConfig)
            await refetch()
            closePopover()
            return
        }

        const firstError = result?.errors?.[0]
        setError(firstError?.messages?.[0] || 'An unknown error occurred.')
    }

    return (
        <>
            <a id={"toggleTableColumnPickerButton"} className="driver-list-button" onClick={togglePopover}>
                {buttonLabel} | <i className="fa fa-table" aria-hidden="true"></i>
            </a>
            <Popover trigger="legacy" placement={placement} isOpen={isOpen} target={"toggleTableColumnPickerButton"} toggle={togglePopover} className="table-preferences-popover">
                <PopoverHeader>
                    <div className="table-preferences-header">
                        <div>
                            <div className="table-preferences-title">Customize Columns</div>
                            <div className="table-preferences-subtitle">Drag to reorder, toggle to show or hide.</div>
                        </div>
                    </div>
                </PopoverHeader>
                <PopoverBody style={{ maxHeight: '360px', overflowY: 'auto' }}>
                    {error && (
                        <Row>
                            <Col><Alert color="danger">{error}</Alert></Col>
                        </Row>
                    )}
                    <div className="table-preferences-list-head">
                        <span>Columns</span>
                        <span>Visible</span>
                    </div>
                    <div className="table-preferences-list">
                        {draftColumnConfig.map((column, index) => (
                            <div
                                key={column.id || index}
                                className={[
                                    'table-preferences-item',
                                    !hasPickerControls(column) ? 'table-preferences-item-disabled' : '',
                                    draggedColumnId === column.id ? 'table-preferences-item-dragging' : '',
                                    dropIndicatorIndex === index ? 'table-preferences-item-drop-target' : '',
                                ].filter(Boolean).join(' ')}
                                draggable={canReorderColumn(column)}
                                onDragStart={(event) => handleDragStart(event, index)}
                                onDragOver={(event) => handleDragOver(event, index)}
                                onDrop={(event) => handleDrop(event, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <div className="table-preferences-item-main">
                                    <span className="drag-btn">
                                        <i className="fa fa-bars" aria-hidden="true"></i>
                                    </span>
                                    <div className="table-preferences-item-copy">
                                        <div className="table-preferences-item-name">{column.name}</div>
                                        <div className="table-preferences-item-meta">
                                            {!canToggleColumnVisibility(column) && !canReorderColumn(column)
                                                ? ''
                                                : canReorderColumn(column) && !canToggleColumnVisibility(column)
                                                    ? 'Can change order'
                                                    : !canReorderColumn(column) && canToggleColumnVisibility(column)
                                                        ? 'Can change visibility' : 'Can change visibility & order'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <label className="table-preferences-toggle">
                                    <input type="checkbox" checked={isColumnVisible(column)} disabled={!canToggleColumnVisibility(column)} onChange={() => toggleColumnVisibility(column.id)} />
                                    <span className="table-preferences-toggle-slider"></span>
                                </label>
                            </div>
                        ))}
                    </div>
                    {(loading || saving) && (
                        <Row>
                            <Col xs={12}><Progress animated color="info" value="100" /></Col>
                        </Row>
                    )}
                    <div className="table-preferences-actions">
                        <Button
                            className="reset-view-btn"
                            onClick={resetToDefault}
                            disabled={saving || loading || draftConfigSignature === defaultConfigSignature}
                        >
                            Reset to Default
                        </Button>
                        <Button className="cancel-view-btn" onClick={closePopover} disabled={saving}>
                            Cancel
                        </Button>
                        <Button className="save-view-btn" onClick={savePreference} disabled={saving || loading}>
                            Save View
                        </Button>
                    </div>
                </PopoverBody>
            </Popover>
        </>
    )
}

export default TableColumnPreferenceControl
